///
/// Where a signed-in user is sent, and where logout returns - chosen by the host
/// the user signed in through.
///
/// WHY: one deployment (App Service aramark-sso-dev) answers on two hostnames
/// that serve two different audiences:
///   - araphillytna.tibersoft.com          -> Aramark's Philly environment
///     (what Aramark calls "QA"), which must reach the legacy Philly TNA;
///   - aramark-sso-dev.azurewebsites.net   -> the AraTrack IQ POC (TNA-98),
///     which must reach the new app.
/// The redirect used to be one build-time value, so pointing it at the POC sent
/// every Philly user to the POC as well (reported by Aramark 2026-09-29).
///
/// This works because Auth0 returns the user to the host they started on: the
/// dev environment's redirect_uri is window.location.origin.
///
/// SAFE DEFAULT: any host without an explicit override gets the environment's
/// own targets - for this build, the legacy Philly TNA. Only a host named in
/// `hostOverrides` is sent anywhere else, so an unexpected hostname can never
/// land a client user in the POC.
///
export interface SsoTargets {
  /// Base URL the user is redirected to after sign-in; the approval keys are appended.
  redirectUrlforTNA: string;
  /// Where Auth0 returns the user after logout.
  logoutUri: string;
}

export interface SsoHostOverride extends SsoTargets {
  /// Exact hostname (no scheme, no port) this override applies to.
  host: string;
}

export function resolveSsoTargets(
  hostname: string,
  defaults: SsoTargets,
  overrides: readonly SsoHostOverride[] = [],
): SsoTargets {
  const host = hostname.toLowerCase();
  const hit = overrides.find((o) => o.host.toLowerCase() === host);
  return hit === undefined
    ? defaults
    : { redirectUrlforTNA: hit.redirectUrlforTNA, logoutUri: hit.logoutUri };
}

///
/// The overrides declared by the active environment file, if any.
///
/// Read through a loose type on purpose: only environment.development.ts
/// declares `hostOverrides`. The QA and production environment files are left
/// untouched, and for them this returns [] - their behaviour is unchanged.
///
export function hostOverridesOf(environment: object): readonly SsoHostOverride[] {
  const value = (environment as Record<string, unknown>)['hostOverrides'];
  return Array.isArray(value) ? (value as SsoHostOverride[]) : [];
}

///
/// The targets for the page's own host, from an environment file's values.
/// Every redirect and logout in the app goes through this, so no code path can
/// read the build-time default directly and skip the per-host choice.
///
export function ssoTargetsFor(
  environment: { redirectUrlforTNA: string; auth: { logoutUri: string } },
  hostname: string = window.location.hostname,
): SsoTargets {
  return resolveSsoTargets(
    hostname,
    { redirectUrlforTNA: environment.redirectUrlforTNA, logoutUri: environment.auth.logoutUri },
    hostOverridesOf(environment),
  );
}
